import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { API_BASE, fetchWithTimeout } from "../config";
import { handleInvalidToken } from "../utils/auth";
import Avatar from "../components/Avatar";
import { formatPlayerName } from "../utils/nameFormat";
import { useResponsive } from "../hooks/useResponsive";
import { useLanguage } from "../i18n";

// Import sport background images
function importAllSportImages(r) {
  const images = {};
  r.keys().forEach((key) => {
    const sportName = key.replace('./', '').replace(/-image\.(png|jpe?g|webp|svg)$/, '');
    images[sportName] = r(key);
  });
  return images;
}
const sportBackgrounds = importAllSportImages(
  require.context("../images/sports", false, /-image\.(png|jpe?g|webp|svg)$/)
);

export default function LeagueDetailPage() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Dynamic responsive hook
  const isMobile = useResponsive(768);
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [joinMsg, setJoinMsg] = useState("");
  const [members, setMembers] = useState([]);
  const [standings, setStandings] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('current'); // 'overall' | 'current' | numeric id
  const [games, setGames] = useState({ upcoming: [], completed: [] });
  const [loadingExtras, setLoadingExtras] = useState(true);
  const [joining, setJoining] = useState(false);
  const [isMemberByMe, setIsMemberByMe] = useState(null); // null = unknown, true/false = known
  const [myOpenMatch, setMyOpenMatch] = useState(null);
  const [matchSearching, setMatchSearching] = useState(false);
  const [hasWeeklyMatch, setHasWeeklyMatch] = useState(false);
  // Match-Formular
  const [opponentId, setOpponentId] = useState("");
  const [kickoffLocal, setKickoffLocal] = useState("");

  const token = localStorage.getItem("token");

  const myId = useMemo(() => {
    if (!token) return null;
    try {
      const parts = token.split(".");
      if (parts.length < 2) return null;
      const payload = JSON.parse(atob(parts[1]));
      return payload?.user?.id ?? payload?.userId ?? payload?.sub ?? payload?.id ?? null;
    } catch {
      return null;
    }
  }, [token]);

  function getMemberUserId(m) {
    if (!m) return null;
    return m.user_id ?? m.userId ?? m.member_user_id ?? m.memberUserId ?? m.member_id ?? m.memberId ?? m.id ?? null;
  }

  const amMember = useMemo(() => {
    if (isMemberByMe != null) return !!isMemberByMe;
    if (!myId) return false;
    return (members || []).some(m => String(getMemberUserId(m)) === String(myId));
  }, [isMemberByMe, members, myId]);

  const leaguePublicState = league?.publicState ?? league?.public_state ?? league?.visibility ?? "";
  const joinPolicyRaw = league?.joinPolicy ?? league?.join_policy ?? "";
  const joinPolicy = String(joinPolicyRaw || "").toLowerCase();

  const isPublicLeague = useMemo(() => {
    const state = String(leaguePublicState || "").toLowerCase();
    if (!state && !joinPolicy) return true;
    if (["public", "open", "visible", "listed"].includes(state)) return true;
    if (["hidden", "private", "closed"].includes(state)) return false;
    if (["public", "open", "auto", "automatic"].includes(joinPolicy)) return true;
    if (["closed", "manual", "invite", "private"].includes(joinPolicy)) return false;
    return !!(league?.isPublic || league?.public);
  }, [leaguePublicState, joinPolicy, league]);

  const isOpenLeague = useMemo(() => {
    if (["open", "public", "auto", "automatic"].includes(joinPolicy)) return true;
    if (["closed", "manual", "invite", "private"].includes(joinPolicy)) return false;
    const state = String(leaguePublicState || "").toLowerCase();
    if (!state && !joinPolicy) return true;
    if (["open", "public"].includes(state)) return true;
    if (["hidden", "private", "closed"].includes(state)) return false;
    return !!league?.autoJoin;
  }, [joinPolicy, leaguePublicState, league]);

  const joinAvailabilityUnknown = String(leaguePublicState || "").trim() === "" && joinPolicy === "";

  const canAttemptJoin = !amMember && (isOpenLeague || isPublicLeague || joinAvailabilityUnknown);

  async function runMatchSearch({ auto = false, refresh = true } = {}) {
    const t = localStorage.getItem("token");
    if (!t) throw new Error("Nicht eingeloggt");
    const resp = await fetchWithTimeout(`${API_BASE}/leagues/${leagueId}/match-search`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${t}`
      },
      body: JSON.stringify({ auto: !!auto }),
      timeout: 8000
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);

    if (data.match && data.match.id) {
      setMyOpenMatch(data.match);
      setGames(prev => {
        const prevUpcoming = Array.isArray(prev?.upcoming) ? prev.upcoming : [];
        const deduped = prevUpcoming.filter(m => String(m.id) !== String(data.match.id));
        return {
          ...prev,
          upcoming: [...deduped, data.match]
        };
      });
    }

    if (refresh) {
      await reloadExtras();
      await fetchMyOpenMatch();
      await fetchMyWeeklyStatus();
    }

    return data;
  }

  const opponents = useMemo(() => {
    return (members || [])
      .map(m => {
        const uid = getMemberUserId(m);
        if (!uid) return null;
        const name = (m.firstname && m.lastname)
          ? `${m.firstname} ${m.lastname}`
          : (m.name || m.username || m.displayName || `User ${uid}`);
        return { id: uid, name };
      })
      .filter(Boolean)
      .filter(o => String(o.id) !== String(myId));
  }, [members, myId]);

  const standingsWithMembers = useMemo(() => {
    const baseRows = (standings || []).map((row, idx) => {
      // Extract user ID from key (e.g., "u:1" -> "1") or use other ID fields
      let uid;
      if (row.key && row.key.startsWith('u:')) {
        uid = row.key.substring(2);
      } else {
        uid = String(
          row.user_id ??
          row.userId ??
          row.member_id ??
          row.memberId ??
          ((row.user || {}).id) ??
          (row.key != null ? row.key : idx)
        );
      }
      return {
        ...row,
        _uid: uid,
        name: row.name || row.displayName || row.username || row.key || `User ${uid}`,
        _order: idx
      };
    });

    const existingIds = new Set(baseRows.map(r => r._uid));
    const fillerRows = (members || [])
      .map(m => {
        const uid = String(getMemberUserId(m));
        if (!uid) return null;
        if (existingIds.has(uid)) return null;
        const name = (m.firstname && m.lastname)
          ? `${m.firstname} ${m.lastname}`
          : (m.name || m.username || `User ${uid}`);
        return {
          _uid: uid,
          name,
          played: 0,
          won: 0,
          drawn: 0,
          lost: 0,
          gf: 0,
          ga: 0,
          gd: 0,
          points: 0,
          rank: null,
          _order: Number.MAX_SAFE_INTEGER,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));

    const merged = [...baseRows, ...fillerRows];
    return merged
      .map((row, idx) => ({
        ...row,
        rank: row.rank ?? (row.points || row.won || row.played ? idx + 1 : "–"),
      }))
      .sort((a, b) => {
        if (a._order === b._order) return a.name.localeCompare(b.name, 'de');
        return a._order - b._order;
      });
  }, [standings, members]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErr("");
    fetch(`${API_BASE}/leagues/${leagueId}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(async j => {
        if (mounted) setLeague(j);
        // After league loads, check membership via /me/leagues if we have a token
        if (mounted) {
          try {
            const t = localStorage.getItem("token");
            if (t) {
              const ml = await fetch(`${API_BASE}/me/leagues`, { headers: { Authorization: `Bearer ${t}` } })
                .then(r => (r.ok ? r.json() : []))
                .catch(() => []);
              const found = Array.isArray(ml) && ml.some(l => String(l.id || l.leagueId) === String(leagueId));
              setIsMemberByMe(!!found);
            } else {
              setIsMemberByMe(false);
            }
          } catch {
            if (mounted) setIsMemberByMe(false);
          }
        }
      })
      .catch(e => mounted && setErr(e.message || "Fehler"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [leagueId]);

  // fetch my open match for this league
  async function fetchMyOpenMatch() {
    try {
      const t = localStorage.getItem('token');
      if (!t) { setMyOpenMatch(null); return; }
      const r = await fetch(`${API_BASE}/leagues/${leagueId}/my-open-match`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) { setMyOpenMatch(null); return; }
      const j = await r.json().catch(() => null);
      setMyOpenMatch(j || null);
    } catch (e) {
      setMyOpenMatch(null);
    }
  }

  // fetch my weekly status for this league
  async function fetchMyWeeklyStatus() {
    try {
      const t = localStorage.getItem('token');
      if (!t) { setHasWeeklyMatch(false); return; }
      const r = await fetch(`${API_BASE}/leagues/${leagueId}/my-weekly-status`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) { setHasWeeklyMatch(false); return; }
      const j = await r.json().catch(() => ({ hasWeeklyMatch: false }));
      setHasWeeklyMatch(!!j?.hasWeeklyMatch);
    } catch (e) {
      setHasWeeklyMatch(false);
    }
  }

  async function reloadExtras() {
    setLoadingExtras(true);
    try {
      const [m, sList, g] = await Promise.all([
        fetch(`${API_BASE}/leagues/${leagueId}/members`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => (r.ok ? r.json() : []))
          .catch(() => []),
        fetch(`${API_BASE}/leagues/${leagueId}/seasons`)
          .then(r => (r.ok ? r.json() : []))
          .catch(() => []),
        fetch(`${API_BASE}/leagues/${leagueId}/games`)
          .then(r => (r.ok ? r.json() : { upcoming: [], completed: [] }))
          .catch(() => ({ upcoming: [], completed: [] }))
      ]);
      setMembers(Array.isArray(m) ? m : []);
      setSeasons(Array.isArray(sList) ? sList : []);
      // initial standings load (current season) in classic table format
      try {
        const r = await fetch(`${API_BASE}/leagues/${leagueId}/standings?format=table`);
        const j = await r.json().catch(() => []);
        setStandings(Array.isArray(j) ? j : []);
      } catch { setStandings([]); }
      setGames({ upcoming: g.upcoming || [], completed: g.completed || [] });
    } finally {
      setLoadingExtras(false);
    }
  }

  useEffect(() => {
    // Lade Extras erst, wenn die Liga erfolgreich geladen wurde.
    if (league) reloadExtras();
  }, [league, token]); // <-- abhängig von 'league' statt nur leagueId
  // reload standings on season change (classic table format)
  useEffect(() => {
    if (!leagueId) return;
    (async () => {
      try {
        let url = `${API_BASE}/leagues/${leagueId}/standings?format=table`;
        if (selectedSeasonId === 'overall') url = `${url}&scope=overall`;
        else if (selectedSeasonId && selectedSeasonId !== 'current') url = `${url}&seasonId=${selectedSeasonId}`;
        const r = await fetch(url);
        const j = await r.json().catch(() => []);
        setStandings(Array.isArray(j) ? j : []);
      } catch { setStandings([]); }
    })();
  }, [leagueId, selectedSeasonId]);

  useEffect(() => {
    // whenever league or membership changes, refresh my open match
    fetchMyOpenMatch();
    fetchMyWeeklyStatus();
  }, [leagueId, isMemberByMe]);

  async function handleJoin() {
    try {
      setJoining(true);
      const token = localStorage.getItem("token");
      const r = await fetchWithTimeout(`${API_BASE}/leagues/${leagueId}/join`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);

      const baseMsg = j.joined ? t('league.join.ok') : (j.message || t('league.join.alreadyMember'));
      let extrasMsg = "";
      if (j.matchAction === "paired") extrasMsg = ` ${t('league.join.paired')}`;
      else if (j.matchAction === "created") extrasMsg = ` ${t('league.join.created')}`;
      else if (j.matchAction === "existing") extrasMsg = ` ${t('league.join.existing')}`;
      else if (j.matchAction === "error") extrasMsg = ` ${t('league.join.error', { error: j.matchError || 'unknown' })}`;
      setJoinMsg(`${baseMsg}${extrasMsg}`.trim());

      if (j.match && j.match.id) {
        setGames(prev => {
          const prevUpcoming = Array.isArray(prev?.upcoming) ? prev.upcoming : [];
          const deduped = prevUpcoming.filter(m => String(m.id) !== String(j.match.id));
          return {
            ...prev,
            upcoming: [...deduped, j.match]
          };
        });
      }

      let extrasReloaded = false;
      const shouldTriggerSearch = j.joined && (!j.match || !j.match.id);

      if (j && j.joined) setIsMemberByMe(true);

      if (j && j.joined && j.match && j.match.id) {
        await fetchMyOpenMatch();
        await fetchMyWeeklyStatus();
        await reloadExtras();
        extrasReloaded = true;
      }

      if (shouldTriggerSearch) {
        try {
          const result = await runMatchSearch({ auto: true });
          if (result && result.action) {
            let autoMsg = "";
            if (result.action === "joined") autoMsg = ` ${t('league.join.auto.joined')}`;
            else if (result.action === "created") autoMsg = ` ${t('league.join.auto.created')}`;
            else if (result.action === "paired") autoMsg = ` ${t('league.join.auto.paired')}`;
            else if (result.action === "skipped") autoMsg = ` ${t('league.join.auto.skipped')}`;
            if (autoMsg) setJoinMsg(prev => `${prev}${autoMsg}`.trim());
          }
          extrasReloaded = true;
        } catch (autoErr) {
          const msg = autoErr?.message || String(autoErr);
          if (!/429|Weekly match limit/i.test(msg)) {
            setJoinMsg(prev => `${prev} (Matchsuche: ${msg})`.trim());
          }
        }
      }

      if (!extrasReloaded) {
        await reloadExtras();
        if (j && j.joined) {
          await fetchMyOpenMatch();
          await fetchMyWeeklyStatus();
        }
      }
    } catch (e) {
      setJoinMsg(`Beitritt fehlgeschlagen: ${e.message || e}`);
      alert(`Beitritt fehlgeschlagen: ${e.message || e}`);
    } finally {
      setJoining(false);
    }
  }

  async function handleLeave() {
    try {
      setJoining(true);
      const token = localStorage.getItem("token");
      const r = await fetchWithTimeout(`${API_BASE}/leagues/${leagueId}/leave`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setJoinMsg(j.left ? "Ausgetreten" : (j.message || "Nicht Mitglied"));
      await reloadExtras();
      // Update membership flag so UI toggles the buttons correctly
      if (j && j.left) setIsMemberByMe(false);
    } catch (e) {
      setJoinMsg(`Austritt fehlgeschlagen: ${e.message || e}`);
      alert(`Austritt fehlgeschlagen: ${e.message || e}`);
    } finally {
      setJoining(false);
    }
  }

  function formatKickoff(match) {
    if (!match) return "Noch kein Termin";
    const raw = match.kickoff_at || match.kickoffAt || match.date || match.scheduled_at || match.scheduledAt;
    if (!raw || (typeof raw === "string" && raw.trim() === "")) return "Noch kein Termin";
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return "Noch kein Termin";
    return dt.toLocaleString('de-DE');
  }

  // Helpers for user IDs and display
  function toNumericId(v) {
    if (v == null) return null;
    const m = String(v).match(/\d+/);
    return m ? m[0] : null;
  }
  function initialsFor(name) {
    const s = String(name || "").trim();
    if (!s) return "?";
    const p = s.split(/\s+/);
    const a = (p[0]?.[0] || "").toUpperCase();
    const b = (p[1]?.[0] || "").toUpperCase();
    return (a + b) || a || "?";
  }

  // Fast lookup of member meta by numeric user id
  const memberById = useMemo(() => {
    const map = new Map();
    (members || []).forEach(m => {
      const uid = toNumericId(getMemberUserId(m));
      if (uid) map.set(String(uid), m);
    });
    return map;
  }, [members]);

  // Build last-5 form (W/U/N) for each user from completed games
  const formByUserId = useMemo(() => {
    const map = new Map();
    const completed = Array.isArray(games?.completed) ? [...games.completed] : [];
    completed.sort((a, b) => {
      const da = new Date(a.kickoff_at || a.kickoffAt || a.date || 0).getTime();
      const db = new Date(b.kickoff_at || b.kickoffAt || b.date || 0).getTime();
      return db - da; // newest first
    });
    const toId = (g, side) => toNumericId(g?.[`${side}_id`] ?? g?.[`${side}Id`] ?? g?.[side]);
    const gScore = (g, k) => g?.[k] ?? g?.[k.replace(/_/, "")] ?? null; // home_score -> homeScore
    completed.forEach(g => {
      const hId = toId(g, 'home');
      const aId = toId(g, 'away');
      const hs = gScore(g, 'home_score');
      const as = gScore(g, 'away_score');
      if (hId && aId && hs != null && as != null) {
        let hRes = 'U', aRes = 'U';
        if (Number(hs) > Number(as)) { hRes = 'W'; aRes = 'N'; }
        else if (Number(hs) < Number(as)) { hRes = 'N'; aRes = 'W'; }
        if (!map.has(hId)) map.set(hId, []);
        if (!map.has(aId)) map.set(aId, []);
        map.get(hId).push(hRes);
        map.get(aId).push(aRes);
      }
    });
    // keep only last 5
    for (const [k, arr] of map.entries()) map.set(k, arr.slice(0, 5));
    return map;
  }, [games?.completed]);
  async function createMatch(e) {
    e && e.preventDefault();
    try {
      if (!opponentId) throw new Error(t('league.pickOpponent'));
      if (!kickoffLocal) throw new Error(t('league.pickDateTime'));
      const token = localStorage.getItem("token");
      const iso = new Date(kickoffLocal).toISOString();
      const r = await fetch(`${API_BASE}/leagues/${leagueId}/matches`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ opponent_user_id: Number(opponentId), kickoff_at: iso }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      // optional: Reload Extras oder navigieren
      await reloadExtras();
      setOpponentId("");
      setKickoffLocal("");
      // Wenn ID geliefert wird, navigieren
      if (j && j.id) navigate(`/matches/${j.id}`);
    } catch (e) {
      alert(t('league.createMatchFailed', { error: (e.message || e) }));
    }
  }

  if (loading) return <div style={{ padding: 16 }}>{t('league.loading')}</div>;
  if (err) {
    if (handleInvalidToken(err, navigate)) return null;
    return <div style={{ padding: 16, color: "crimson" }}>{t('match.errorPrefix')}: {err}</div>;
  }
  if (!league) return <div style={{ padding: 16 }}>{t('league.noData')}</div>;

  // Mobile responsive styles (now using dynamic hook)
  const wrap = { 
    padding: isMobile ? 8 : 16, 
    color: '#e8efe8', 
    fontFamily: 'Inter, system-ui, sans-serif' 
  };
  const card = { 
    background: '#0f2a20', 
    borderRadius: isMobile ? 12 : 16, 
    boxShadow: isMobile ? '0 8px 24px rgba(0,0,0,0.4)' : '0 14px 36px rgba(0,0,0,0.5)' 
  };
  const pad = { padding: isMobile ? 12 : 16 };
  const pill = { 
    display: 'inline-block', 
    padding: isMobile ? '5px 10px' : '6px 12px', 
    borderRadius: 999, 
    border: '1px solid #2f6b57', 
    background: '#0e2a22', 
    color: '#dfe',
    fontSize: isMobile ? 12 : 14
  };
  const small = { fontSize: isMobile ? 11 : 12, color: '#a6bfb3' };

  // Get sport-specific background image
  const getSportBackground = () => {
    if (!league?.sport) return null;
    const normalized = league.sport.toLowerCase().replace(/\s+/g, '-');
    return sportBackgrounds[normalized] || null;
  };
  const sportBg = getSportBackground();

  return (
    <div style={wrap}>
      {/* Hero */}
      <div style={{ 
        ...card, 
        paddingBottom: 0,
        ...(sportBg && {
          backgroundImage: `linear-gradient(to bottom, rgba(15, 42, 32, 0.4), rgba(15, 42, 32, 0.55)), url(${sportBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        })
      }}>
        <div style={{ ...pad, paddingTop: isMobile ? 20 : 32, paddingBottom: isMobile ? 20 : 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 26 }}>🏆</div>
            <div style={{ fontSize: 28, fontWeight: 900 }}>{league.name}</div>
          </div>
          <div style={{ marginTop: 4, ...small }}>
            <Link to={`/cities/${league.cityId}`} style={{ color: '#cfe' }}>{league.city}</Link>
            {" · "}
            <Link to={`/sports/${league.sportId}`} style={{ color: '#cfe' }}>{league.sport}</Link>
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {canAttemptJoin && (
              <button onClick={handleJoin} disabled={!token || joining} style={pill}>
                {joining ? "Beitritt..." : "Teilnehmen"}
              </button>
            )}
            {amMember && (
              <button onClick={handleLeave} disabled={!token || joining} style={pill}>
                {joining ? "Austritt..." : "Austreten"}
              </button>
            )}
            {joinMsg && <span style={{ color: joinMsg.includes("fehl") ? "crimson" : "#9fd" }}>{joinMsg}</span>}
          </div>
        </div>
        {/* quick stats */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
          gap: isMobile ? 8 : 12, 
          padding: isMobile ? 16 : 24,
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: isMobile ? 20 : 26, 
              fontWeight: 900 
            }}>{(standingsWithMembers || []).reduce((a,b)=>a + (b.played||0),0)}</div>
            <div style={{ color: '#bcd', fontSize: isMobile ? 12 : 14 }}>Spiele</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: isMobile ? 20 : 26, 
              fontWeight: 900 
            }}>{(games.completed||[]).reduce((a,b)=>a + ((b.home_score||0)+(b.away_score||0)),0)}</div>
            <div style={{ color: '#bcd', fontSize: isMobile ? 12 : 14 }}>Tore</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: isMobile ? 20 : 26, 
              fontWeight: 900 
            }}>{members.length}</div>
            <div style={{ color: '#bcd', fontSize: isMobile ? 12 : 14 }}>Mitglieder</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: isMobile ? 20 : 26, 
              fontWeight: 900 
            }}>{(games.upcoming||[]).length}</div>
            <div style={{ color: '#bcd', fontSize: isMobile ? 12 : 14 }}>Anstehend</div>
          </div>
        </div>
        {/* league highlights */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: isMobile ? 12 : 16,
          padding: isMobile ? 16 : 24,
          paddingTop: isMobile ? 12 : 16,
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <div>
            <div style={{ fontSize: isMobile ? 11 : 12, color: '#9ab', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
              {t('league.leader')}
            </div>
            {standingsWithMembers && standingsWithMembers.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 20 }}>👑</div>
                <div>
                  <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: '#ffd700' }}>
                    {standingsWithMembers[0].username || 'N/A'}
                  </div>
                  <div style={{ fontSize: isMobile ? 11 : 12, color: '#9ab' }}>
                    {standingsWithMembers[0].points || 0} {t('league.points')} · {standingsWithMembers[0].played || 0} {t('league.games')}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ color: '#7a9', fontSize: isMobile ? 13 : 14 }}>{t('league.noDataYet')}</div>
            )}
          </div>
          <div>
            <div style={{ fontSize: isMobile ? 11 : 12, color: '#9ab', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
              Top Scorer
            </div>
            {standingsWithMembers && standingsWithMembers.length > 0 ? (
              (() => {
                const topScorer = [...standingsWithMembers].sort((a, b) => (b.goals_for || 0) - (a.goals_for || 0))[0];
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 20 }}>⚽</div>
                    <div>
                      <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: '#4af' }}>
                        {topScorer.username || 'N/A'}
                      </div>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#9ab' }}>
                        {topScorer.goals_for || 0} {t('league.goals')}
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div style={{ color: '#7a9', fontSize: isMobile ? 13 : 14 }}>{t('league.noDataYet')}</div>
            )}
          </div>
        </div>
      </div>

      {amMember && (myOpenMatch || hasWeeklyMatch) && (
        <div style={{ marginTop: 8, color: '#ccc' }}>
          Du kannst nur ein offenes Match gleichzeitig haben und nur ein Match pro Woche spielen.
        </div>
      )}

      {/* Match suchen (join-or-create) */}
      {amMember && !myOpenMatch && !hasWeeklyMatch && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={async () => {
              try {
                setMatchSearching(true);
                const result = await runMatchSearch({ auto: false });
                if (result?.action === 'joined') alert(t('league.search.result.joined'));
                else if (result?.action === 'created') alert(t('league.search.result.created'));
                else if (result?.action === 'paired') alert(t('league.search.result.paired'));
                else if (result?.action === 'skipped') alert(t('league.search.result.skipped'));
              } catch (e) {
                alert(t('league.search.failed', { error: (e.message || e) }));
              } finally {
                setMatchSearching(false);
              }
            }}
            disabled={!token || matchSearching}
          >
            {matchSearching ? t('league.searching') : t('league.searchMatch')}
          </button>
        </div>
      )}

      {amMember && myOpenMatch && (
        <div style={{ marginTop: 12 }}>
          {t('league.openMatch.notice')}{' '}
          <Link to={`/matches/${myOpenMatch.id}`}>{t('league.openMatch.toMatch')}</Link>
        </div>
      )}

      {amMember && !myOpenMatch && hasWeeklyMatch && (
        <div style={{ marginTop: 12 }}>
          {t('league.weeklyLimit')}
        </div>
      )}

      {loadingExtras ? (
        <div style={{ ...card, ...pad, marginTop: 16 }}>{t('league.loadingExtra')}</div>
      ) : (
        <>
          {/* Mitglieder */}
          <div style={{ ...card, marginTop: 16 }}>
            <div style={{ ...pad, paddingBottom: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{t('league.table.title')}</div>
              <div style={{ marginTop: 8 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {t('league.table.season')}
                  <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(e.target.value)}>
                    <option value="current">{t('league.table.currentSeason')}</option>
                    {seasons.map(se => <option key={se.id} value={se.id}>{se.name}</option>)}
                    <option value="overall">Overall</option>
                  </select>
                </label>
              </div>
            </div>
            <div style={{ ...pad, paddingTop: 8 }}>
              {standingsWithMembers.length === 0 ? (
                <div style={small}>{t('league.table.noEntries')}</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '2px solid #234' }}>
                        <th style={{ padding: '8px 6px' }}>{t('league.table.place')}</th>
                        <th style={{ padding: '8px 6px' }}>{t('league.table.teamPlayer')}</th>
                        <th style={{ padding: '8px 6px' }}>{t('league.table.played')}</th>
                        <th style={{ padding: '8px 6px' }}>{t('league.table.won')}</th>
                        <th style={{ padding: '8px 6px' }}>{t('league.table.draw')}</th>
                        <th style={{ padding: '8px 6px' }}>{t('league.table.lost')}</th>
                        <th style={{ padding: '8px 6px' }}>{t('league.table.goals')}</th>
                        <th style={{ padding: '8px 6px' }}>{t('league.table.diff')}</th>
                        <th style={{ padding: '8px 6px' }}>{t('league.table.points')}</th>
                        <th style={{ padding: '8px 6px' }}>{t('league.table.form')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standingsWithMembers.map((row, idx) => (
                        <tr key={row._uid || idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <td style={{ padding: '8px 6px' }}>{row.rank ?? (idx + 1)}</td>
                          <td style={{ padding: '8px 6px' }}>
                            {(() => {
                              const uid = toNumericId(row._uid);
                              const m = uid ? memberById.get(String(uid)) : null;
                              const avatarUrl = m?.avatar || m?.avatarUrl || m?.image || m?.imageUrl || m?.profile_image || m?.profileImage || null;
                              const display = row.name || row.key || `User ${uid || ''}`;
                              const avatar = (
                                <span style={{ marginRight: 8, verticalAlign: 'middle', display: 'inline-flex' }}>
                                  <Avatar userId={uid} name={display} src={avatarUrl} size={28} />
                                </span>
                              );
                              return uid ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  {avatar}
                                  <Link to={`/user/${uid}`} style={{ color: '#cfe', textDecoration: 'none' }} title={t('league.openProfile')}>
                                    {display}
                                  </Link>
                                  <span aria-hidden style={{ marginLeft: 6, opacity: 0.7 }}>↗︎</span>
                                </span>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  {avatar}
                                  {display}
                                </span>
                              );
                            })()}
                          </td>
                          <td style={{ padding: '8px 6px' }}>{row.played ?? 0}</td>
                          <td style={{ padding: '8px 6px' }}>{row.won ?? 0}</td>
                          <td style={{ padding: '8px 6px' }}>{row.drawn ?? 0}</td>
                          <td style={{ padding: '8px 6px' }}>{row.lost ?? 0}</td>
                          <td style={{ padding: '8px 6px' }}>{(row.gf ?? 0)}:{(row.ga ?? 0)}</td>
                          <td style={{ padding: '8px 6px' }}>{(row.gd ?? ((row.gf||0) - (row.ga||0)))}</td>
                          <td style={{ padding: '8px 6px' }}>{row.points ?? 0}</td>
                          <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                            {(() => {
                              const uid = toNumericId(row._uid);
                              const form = (uid && formByUserId.get(String(uid))) || [];
                              const color = (r) => (r === 'W' ? '#29e0ad' : r === 'N' ? '#ff6b6b' : '#c4d0ca');
                              return (
                                <span>
                                  {form.map((r, i) => (
                                    <span key={i} style={{ color: color(r), fontWeight: 700, display: 'inline-block', marginRight: 6 }}>{r}</span>
                                  ))}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Spiele */}
          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: '1fr', marginTop: 16 }}>
            <div style={{ ...card }}>
              <div style={{ ...pad, paddingBottom: 8 }}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>Anstehende Spiele</div>
              </div>
              <div style={{ ...pad, paddingTop: 8 }}>
                {games.upcoming.length === 0 ? (
                  <div style={small}>{t('league.noUpcoming')}</div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {games.upcoming.map(g => {
                      const toId = (v) => {
                        if (v == null) return null;
                        if (typeof v === 'number') return String(v);
                        const m = String(v).match(/\d+/);
                        return m ? m[0] : null;
                      };
                      const hId = g.home_user_id || g.homeUserId || g.home_id || g.homeId || toId(g.home);
                      const aId = g.away_user_id || g.awayUserId || g.away_id || g.awayId || toId(g.away);
                      const kx = g.kickoff_at || g.kickoffAt || g.date || null;
                      const when = kx ? new Date(kx).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
                      return (
                        <div key={g.id} className="ml-match" style={{ padding: '10px 2px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <div className="ml-match__side">
                            {hId ? (
                              <Link to={`/user/${hId}`} style={{ display: 'contents' }}>
                                <Avatar userId={hId} name={formatPlayerName(g.home)} size={44} title={g.home} />
                                <span style={{ color: '#cfe', textDecoration: 'none' }}>{formatPlayerName(g.home)}</span>
                              </Link>
                            ) : (
                              <>
                                <Avatar userId={hId} name={formatPlayerName(g.home)} size={44} />
                                <span>{formatPlayerName(g.home)}</span>
                              </>
                            )}
                          </div>
                          <div className="ml-vs">VS</div>
                          <div className="ml-match__side" style={{ justifyContent: 'flex-end' }}>
                            {aId ? (
                              <Link to={`/user/${aId}`} style={{ display: 'contents' }}>
                                <Avatar userId={aId} name={formatPlayerName(g.away)} size={44} title={g.away} />
                                <span style={{ color: '#cfe', textDecoration: 'none' }}>{formatPlayerName(g.away)}</span>
                              </Link>
                            ) : (
                              <>
                                <Avatar userId={aId} name={formatPlayerName(g.away)} size={44} />
                                <span>{formatPlayerName(g.away)}</span>
                              </>
                            )}
                          </div>
                          <div style={{ gridColumn: '1 / -1', color: '#9db', fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
                            <span>{formatKickoff(g)}</span>
                            <span>{when}</span>
                          </div>
                          {g?.id ? (
                            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                              <Link to={`/matches/${g.id}`} style={{ color: '#debc7c', fontWeight: 700, textDecoration: 'none' }}>Details</Link>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div style={{ ...card }}>
              <div style={{ ...pad, paddingBottom: 8 }}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>Vergangene Spiele</div>
              </div>
              <div style={{ ...pad, paddingTop: 8 }}>
                {games.completed.length === 0 ? (
                  <div style={small}>{t('league.noCompleted')}</div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {games.completed.map(g => {
                      const toId = (v) => {
                        if (v == null) return null;
                        if (typeof v === 'number') return String(v);
                        const m = String(v).match(/\d+/);
                        return m ? m[0] : null;
                      };
                      const hId = g.home_user_id || g.homeUserId || g.home_id || g.homeId || toId(g.home);
                      const aId = g.away_user_id || g.awayUserId || g.away_id || g.awayId || toId(g.away);
                      const kx = g.kickoff_at || g.kickoffAt || g.date || null;
                      const when = kx ? new Date(kx).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
                      const score = (g.home_score != null && g.away_score != null) 
                        ? `${g.home_score}:${g.away_score}` 
                        : 'VS';
                      return (
                        <div key={g.id} className="ml-match" style={{ padding: '10px 2px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <div className="ml-match__side">
                            {hId ? (
                              <Link to={`/user/${hId}`} style={{ display: 'contents' }}>
                                <Avatar userId={hId} name={formatPlayerName(g.home)} size={44} title={g.home} />
                                <span style={{ color: '#cfe', textDecoration: 'none' }}>{formatPlayerName(g.home)}</span>
                              </Link>
                            ) : (
                              <>
                                <Avatar userId={hId} name={formatPlayerName(g.home)} size={44} />
                                <span>{formatPlayerName(g.home)}</span>
                              </>
                            )}
                          </div>
                          <div className="ml-vs">{score}</div>
                          <div className="ml-match__side" style={{ justifyContent: 'flex-end' }}>
                            {aId ? (
                              <Link to={`/user/${aId}`} style={{ display: 'contents' }}>
                                <Avatar userId={aId} name={formatPlayerName(g.away)} size={44} title={g.away} />
                                <span style={{ color: '#cfe', textDecoration: 'none' }}>{formatPlayerName(g.away)}</span>
                              </Link>
                            ) : (
                              <>
                                <Avatar userId={aId} name={formatPlayerName(g.away)} size={44} />
                                <span>{formatPlayerName(g.away)}</span>
                              </>
                            )}
                          </div>
                          <div style={{ gridColumn: '1 / -1', color: '#9db', fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
                            <span>{formatKickoff(g)}</span>
                            <span>{when}</span>
                          </div>
                          {g?.id ? (
                            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                              <Link to={`/matches/${g.id}`} style={{ color: '#debc7c', fontWeight: 700, textDecoration: 'none' }}>Details</Link>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

        </>
      )}

      <div style={{ marginTop: 12 }}>
        <Link to="/leagues">{t('league.backToOverview')}</Link>
      </div>
    </div>
  );
}
